import { isEmpty } from 'lodash';

/**
 * @namespace Utils
 * @description Shared evaluator for CMS declared filter conditions. Used by the source result filter drawer and by
 * any dialog it opens, so the two cannot drift apart.
 * @date 2026/08/03
 * @license no license
 * @copywrite Answers In Retirement Limited
 */

/**
 * @name evaluateCondition
 * @description Evaluate a single CMS condition object against a filter model
 * @param {Object} condition The condition, e.g. { name: 'initialAdvance', type: 'greater_than', value: 0 }
 * @param {Object} model The filter model to read the named value from
 * @returns {Boolean} Whether the condition passes
 */
export const evaluateCondition = (condition, model = {}) => {
	const value = model[condition.name];

	switch (condition.type) {
		case 'equals':
			return value == condition.value;
		case 'not_equals':
			return value != condition.value;
		case 'includes':
			return !!value && value.includes(condition.value);
		// isEmpty/notEmpty use lodash, which reports every number and boolean as empty. Use greater_than
		// for numeric filters - these two are only meaningful for arrays, objects and strings.
		case 'isEmpty':
			return isEmpty(value);
		case 'notEmpty':
			return !isEmpty(value);
		case 'greater_than':
			return Number(value) > Number(condition.value);
		default:
			return false;
	}
};

/**
 * @name evaluateConditions
 * @description Evaluate a CMS conditions object. showAll is AND, show is OR, no conditions passes.
 * @param {Object} conditions The conditions object from a CMS form item or option
 * @param {Object} model The filter model to evaluate against
 * @returns {Boolean} Whether the item should show
 */
export const evaluateConditions = (conditions, model = {}) => {
	if (!conditions) return true;
	if (conditions.showAll) return conditions.showAll.every((condition) => evaluateCondition(condition, model));
	if (!conditions.show) return true;
	return conditions.show.some((condition) => evaluateCondition(condition, model));
};
